// src/components/NewsCard.tsx
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Clock, User, Eye, Tag } from 'lucide-react';

interface NewsCardProps {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  category?: string;
  delay?: number;
  isRTL?: boolean;
  featured?: boolean;
}

export const NewsCard = ({ 
  slug, 
  title, 
  excerpt, 
  image, 
  date, 
  category, 
  delay = 0, 
  isRTL = false,
  featured = false
}: NewsCardProps) => {
  // Calculate read time (mock - based on excerpt length)
  const readTime = Math.max(3, Math.ceil(excerpt.length / 200));

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -12 }}
      className={`h-full ${featured ? 'md:col-span-2 lg:col-span-1' : ''}`}
    >
      <Link to={`/news/${slug}`} className="group block h-full">
        <div className="relative h-full rounded-3xl overflow-hidden bg-card border border-border hover:border-primary/60 shadow-xl hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500">
          {/* Featured Badge */}
          {featured && (
            <div className="absolute top-0 left-0 right-0 z-20">
              <div className="bg-gradient-to-r from-primary via-primary to-accent py-2 px-4 text-center">
                <span className="text-xs font-bold text-primary-foreground">
                  ⭐ {isRTL ? 'خبر مميز' : 'Featured News'} ⭐
                </span>
              </div>
            </div>
          )}

          {/* Image Container with Enhanced Overlay */}
          <div className={`relative ${featured ? 'h-96' : 'h-72'} overflow-hidden ${featured ? 'mt-8' : ''}`}>
            <motion.img
              src={image}
              alt={title}
              className="w-full h-full object-cover"
              loading="lazy"
              whileHover={{ scale: 1.08 }}
              transition={{ duration: 0.7 }}
            />
            
            {/* Multi-layer Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/80 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            {/* Category Badge */}
            {category && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: delay + 0.2 }}
                className={`absolute top-6 ${isRTL ? "right-6" : "left-6"} px-4 py-2 rounded-full bg-primary/95 backdrop-blur-sm border border-primary/40 shadow-lg z-10`}
              >
                <div className="flex items-center gap-1.5">
                  <Tag className="h-3 w-3 text-primary-foreground" />
                  <span className="text-xs font-semibold text-primary-foreground">
                    {category}
                  </span>
                </div>
              </motion.div>
            )}

            {/* Date & Read Time Badge */}
            <div className={`absolute bottom-6 ${isRTL ? "right-6 left-6" : "left-6 right-6"} flex items-center gap-3`}>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-lg">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">
                  {new Date(date).toLocaleDateString(
                    isRTL ? 'ar-EG' : 'en-US',
                    { month: 'short', day: 'numeric', year: 'numeric' }
                  )}
                </span>
              </div>

              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-lg">
                <Clock className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">
                  {readTime} {isRTL ? 'دقائق' : 'min read'}
                </span>
              </div>
            </div>
          </div>

          {/* Enhanced Content */}
          <div className={`p-8 ${isRTL ? "text-right" : "text-left"}`}>
            {/* Title */}
            <h3 className={`${featured ? 'text-3xl' : 'text-xl'} font-bold mb-4 line-clamp-2 group-hover:text-primary transition-colors duration-300 leading-tight`}>
              {title}
            </h3>

            {/* Excerpt */}
            <p className={`text-muted-foreground ${featured ? 'text-base' : 'text-sm'} line-clamp-3 mb-6 leading-relaxed`}>
              {excerpt}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between pt-6 border-t border-border/50">
              <motion.span
                whileHover={{ x: isRTL ? -8 : 8 }}
                className="text-primary font-semibold flex items-center gap-2"
              >
                {isRTL ? 'اقرأ المزيد' : 'Read More'}
                <ArrowRight className={`h-5 w-5 ${isRTL ? "rotate-180" : ""}`} />
              </motion.span>

              {/* Views (Mock) */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Eye className="h-4 w-4" />
                <span>{Math.floor(Math.random() * 1000) + 100}</span>
              </div>
            </div>
          </div>

          {/* Shine Effect */}
          <div
            className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent ${
              isRTL
                ? "translate-x-full group-hover:-translate-x-full"
                : "-translate-x-full group-hover:translate-x-full"
            } transition-transform duration-1000`}
          />
        </div>
      </Link>
    </motion.div>
  );
};
